/**
 * Уменьшенные копии слайдов для ленты миниатюр.
 *
 * В ленте миниатюра размером с ноготь брала тот же файл, что и большой слайд:
 * сторона 2560 px — это ~15 МБ в распакованном виде на каждую. Из-за этого в
 * ленте приходилось держать не больше десятка картинок (и слайды после
 * десятого оставались пустыми), а на телефоне картинки убирали совсем.
 *
 * Здесь слайд один раз уменьшается через canvas до стороны в 320 px (~30 КБ) и
 * дальше живёт как blob. Такую ленту можно показывать целиком и на телефоне.
 *
 * Уменьшаем по одному: чтобы сделать копию, браузер всё-таки распаковывает
 * исходник целиком, и десяток одновременных распаковок — ровно то, чего мы
 * избегаем.
 */

/** Длинная сторона уменьшенной копии. Хватает для ленты на любом экране. */
const THUMB_MAX_SIDE = 320;

/** Сколько копий держим. Больше — только у презентаций в сотни слайдов. */
const MAX_CACHED = 80;

/** Если слайд не удалось скачать за это время, считаем попытку неудачной. */
const LOAD_TIMEOUT_MS = 20_000;

const cache = new Map<string, string>();
const pending = new Map<string, Promise<string | null>>();
const failed = new Set<string>();

/** Очередь: одна распаковка исходника за раз. */
let chain: Promise<unknown> = Promise.resolve();

function remember(src: string, url: string): void {
  cache.set(src, url);
  while (cache.size > MAX_CACHED) {
    const oldest = cache.keys().next();
    if (oldest.done) break;
    const stale = cache.get(oldest.value);
    cache.delete(oldest.value);
    if (stale) URL.revokeObjectURL(stale);
  }
}

function shrink(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    let done = false;

    function finish(url: string | null) {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      // Снимаем адрес: иначе Safari держит картинку в памяти до конца загрузки.
      img.removeAttribute("src");
      resolve(url);
    }

    const timer = window.setTimeout(() => finish(null), LOAD_TIMEOUT_MS);

    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return finish(null);
        const k = Math.min(1, THUMB_MAX_SIDE / Math.max(w, h));
        const cw = Math.max(1, Math.round(w * k));
        const ch = Math.max(1, Math.round(h * k));
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");
        if (!ctx) return finish(null);
        // Страница PDF бывает с прозрачным фоном — под jpeg подкладываем белый.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, cw, ch);
        ctx.drawImage(img, 0, 0, cw, ch);
        canvas.toBlob(
          (blob) => {
            // Обнуляем размер — холст освобождает память сразу, не дожидаясь сборки мусора.
            canvas.width = 0;
            canvas.height = 0;
            finish(blob ? URL.createObjectURL(blob) : null);
          },
          "image/jpeg",
          0.78
        );
      } catch {
        finish(null);
      }
    };
    img.onerror = () => finish(null);
    img.src = src;
  });
}

/** Готовая копия, если она уже сделана. Синхронно — чтобы не мигать при прокрутке. */
export function cachedSlideThumb(src: string): string | null {
  const url = cache.get(src);
  if (!url) return null;
  // Освежаем позицию: вытесняем самые давние.
  cache.delete(src);
  cache.set(src, url);
  return url;
}

/**
 * Адрес уменьшенной копии слайда. `null` — уменьшить не вышло (слайд не
 * скачался или браузер не дал холст); в этом случае показывать исходник.
 */
export function slideThumb(src: string): Promise<string | null> {
  const ready = cachedSlideThumb(src);
  if (ready) return Promise.resolve(ready);
  if (failed.has(src)) return Promise.resolve(null);

  const already = pending.get(src);
  if (already) return already;

  const task = () => shrink(src);
  const run = chain.then(task, task).then((url) => {
    pending.delete(src);
    if (!url) {
      failed.add(src);
      return null;
    }
    remember(src, url);
    return url;
  });
  chain = run.catch(() => {});
  pending.set(src, run);
  return run;
}
